import * as React from 'react';
import { getSearchPayload } from '../viewActions';
import './export.scss';
import { clone, cn, debounce } from '../../../utils';
import { API } from '../../../api';
import { sleep } from 'rcd-cpp-workspace/utils/sleep';
import { ViewType } from '../../../enums/ViewType';
import { ToasterWithLink, useToaster } from '../../Toaster/toaster';
import { useFullStory } from '../../App/fullStory';
import { FSEvent } from '../../../enums/FSEvent';
import { useSelector } from '../../../utils/useSelector';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { Op } from '../../../enums/Op';
import { View } from '../../../types/View';
import { UserSetting } from '../../../types/UserSetting';
import { NodeType } from '../../../enums/NodeType';
import { ApiResponseMessage } from '../../../utils/constants';

type Props = {
  searchPayload?: View.SearchPayload;
  viewName?: string;
};

export const Export = (props: Props) => {
  const searchPayload = useSelector(
    s => {
      if (props.searchPayload) return props.searchPayload;
      if (s.view?.selected?.props?.isMyTasks) {
        return null;
      }
      return getSearchPayload(s.view, s.view?.selected, s.planning)?.payload;
    },
    (a, b) => deepEquals(a, b)
  );
  const viewSetting = useSelector(s => {
    const userSettings = s.planning.userSettings;
    const viewRefId = s.view.selected?.props?.refId;
    if (viewRefId && userSettings) {
      return userSettings[viewRefId];
    }
    return null;
  });
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const viewName = useSelector(
    s => props.viewName || s.view?.selected?.props?.name
  );
  const { captureEvent } = useFullStory();
  if (!searchPayload || !getAPI) return null;
  const disabled = searchPayload.missingRequiredFilters?.length > 0;
  const { createToaster } = useToaster(ToasterWithLink);
  const withMsg = (msg: string) => ({
    header: viewName,
    messages: [msg]
  });
  const onClick = debounce(async () => {
    const { exportData, getUserExport } = getAPI();
    const toaster = createToaster();
    const refId = toaster.id;

    toaster.show(withMsg('Generating CSV File...'), true);
    const viewType =
      searchPayload?.view?.props?.type == ViewType.Kanban ? 'board' : 'grid';
    captureEvent(FSEvent.Export, { viewType });
    exportData(refId, toExportPayload(searchPayload, viewSetting)).catch(e => {
      if (e.message != ApiResponseMessage.EndpointRequestTimedOut) {
        toaster.error(withMsg(e.message));
      }
    });

    pollUserExport(refId, { getUserExport }).then(userExport => {
      if (!userExport?.props) {
        toaster.error(
          withMsg('Failed to generate CSV File, Please try again later.')
        );
      } else {
        const { url, message, fileName } = userExport.props;
        if (url) {
          toaster.show(withMsg('Your download will start in few seconds...'));
          downloadFile(url, fileName)
            .then(() => {
              toaster.success(
                withMsg(
                  'Please check your download folder for the exported csv file.'
                )
              );
            })
            .catch(err => {
              toaster.error(
                withMsg(
                  'Failed to download your exported file, Please try again later.'
                )
              );
            });
        } else {
          toaster.error(withMsg(message));
        }
      }
    });
  }, 300);
  return (
    <div
      className={cn('export-link', { 'export-disabled': disabled })}
      onClick={disabled ? null : onClick}
    >
      Export
    </div>
  );
};

const pollUserExport = async (
  refId: string,
  { getUserExport }: Pick<API, 'getUserExport'>
) => {
  await sleep(3000);
  const maxAttempts = 180;
  let attempts = 0;
  while (++attempts < maxAttempts) {
    const userExport = await getUserExport(refId);
    const status = userExport?.props?.status;
    if (['Failed', 'Done'].includes(status)) return userExport;
    await sleep(5000);
  }
};

const downloadFile = async (url: string, fileName: string) => {
  const r = await fetch(url);
  const b = await r.blob();
  let link = document.createElement('a');
  let href = URL.createObjectURL(b);
  link.setAttribute('href', href);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const toExportPayload = (
  searchPayload: View.SearchPayload,
  viewSetting: UserSetting
): View.SearchPayload => {
  searchPayload.limit = 1000000;
  if (searchPayload.view.props.type != ViewType.Tabular) {
    const v = clone(searchPayload.view);
    const groupSorting: View.SortByField[] = [];
    if (viewSetting?.props?.hideSwimlanes && v.props.groupBy.length > 1) {
      v.props.groupBy.pop();
    }
    if (v.props.groupBy?.length) {
      if (v.props.data == NodeType.Item) {
        v.card.props.fields = v.card.props.fields.filter(
          f => f.field != 'Item.itemType'
        );
        v.card.props.fields.unshift({ field: 'Item.itemType' });
      }
      v.props.groupBy.forEach(g => {
        if (g.field) {
          v.card.props.fields.unshift({ field: g.field });
          groupSorting.push({ field: g.orderBy || g.field });
        }
        if (g.groupByParentField) {
          v.card.props.fields.unshift({ field: g.groupByParentField });
          groupSorting.push({
            field: g.groupByParentOrderBy || g.groupByParentField
          });
        }
      });
      searchPayload.filters.push({
        field: v.props.groupBy[0].field,
        conditions: [
          {
            [Op.isNotEmpty]: true
          }
        ]
      });
    }
    if (v.props.type != ViewType.Report) {
      v.props.type = ViewType.Tabular;
    }

    v.props.groupBy = null;
    searchPayload.view = v;
    if (groupSorting.length) {
      if (!searchPayload.sortBy) searchPayload.sortBy = [];
      groupSorting.forEach(s => {
        searchPayload.sortBy.unshift(s);
      });
    }
  }
  return searchPayload;
};
